import { z } from 'zod';

// SHCT-588 (reverses SHCT-208): 종료 사유 4종 = 완료 / 탈퇴 / 재입원 / 기타.
// PRD 03 §3.4 (Req Clarification Q9) closeReason 4종 = COMPLETED·WITHDRAWN·REHOSPITALIZED·OTHER;
// prototype 관리종료 모달과 정합. 탈퇴는 여기서 staff가 선택 가능한 종료 사유로 복원됐다(Option A):
// Patient status→CLOSED, closeReason=WITHDRAWN 만 기록한다. 앱 로그인 계정(AppAccount)은 건드리지
// 않으므로 다른 3개 사유와 동일하게 동작한다(환자 앱 자가 탈퇴와 달리 로그인 차단 없음).
// ⚠️ BE 의존: BE CLOSE_REASONS 집합에 WITHDRAWN 추가 필요(현재 close()가 거부) — 별도 BE 티켓 참조.
export const CLOSE_REASONS = [
	'COMPLETED',
	'WITHDRAWN',
	'REHOSPITALIZED',
	'OTHER',
] as const;

export const closePatientSchema = z.object({
	reason: z.enum(CLOSE_REASONS),
	endDate: z.string().min(1, 'validation:required'),
	memo: z.string().optional(),
});

/**
 * Returns a refined schema that validates endDate is between dischargeDate and today (inclusive).
 * @param dischargeDate - The patient's discharge date (YYYY-MM-DD).
 */
export function createClosePatientSchema(dischargeDate: string) {
	const today = new Date().toISOString().split('T')[0];
	return closePatientSchema.superRefine((data, ctx) => {
		// SHCT-208: 사유가 '기타(OTHER)'면 메모 필수 (BE PATIENT_CLOSE_MEMO와 정합).
		if (data.reason === 'OTHER' && !(data.memo ?? '').trim()) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'validation:closePatient.memo.requiredWhenOther',
				path: ['memo'],
			});
		}
		if (!data.endDate) return;
		if (data.endDate < dischargeDate) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'validation:closePatient.endDate.beforeDischarge',
				path: ['endDate'],
			});
		}
		if (data.endDate > today) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'validation:closePatient.endDate.afterToday',
				path: ['endDate'],
			});
		}
	});
}

export type ClosePatientFormValues = z.infer<typeof closePatientSchema>;
